import { useMutation } from '@apollo/client';
import { InsertQuestionnaireInvitesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type InsertQuestionnaireInvitesCompatibilityResult,
  type InsertQuestionnaireInvitesInput,
  useInsertQuestionnaireInvitesTRPC,
} from './useInsertQuestionnaireInvitesTRPC';

/**
 * Flag-gated wrapper for the `insertQuestionnaireInvites` mutation (COI-170).
 * Defaults to the legacy Apollo mutation and delegates to the tRPC variant when
 * the `trpc` feature flag is enabled. Consumers import THIS, never the `*TRPC`
 * variant directly.
 *
 * A twin rather than a `createMutationHook` config because the tRPC procedure
 * goes through `executeAsyncRequest` (the data-layer processor carries an
 * `ObjectEventStrategy` for the node-backed `third_party_response`), which the
 * factory does not support.
 */
export const useInsertQuestionnaireInvites = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertQuestionnaireInvitesDocument,
    {
      update: (cache) => {
        evictField(cache, 'third_party_response');
      },
    }
  );

  const trpcMutation = useInsertQuestionnaireInvitesTRPC();

  const insertQuestionnaireInvites = async (
    input: InsertQuestionnaireInvitesInput
  ): Promise<InsertQuestionnaireInvitesCompatibilityResult> => {
    if (trpcEnabled) {
      return trpcMutation.insertQuestionnaireInvites(input);
    }

    const result = await insertGraphQL({
      variables: {
        thirdPartyId: input.ThirdPartyId,
        users: input.UserEmails,
        message: input.Message,
        questionnaires: input.QuestionnaireTemplateVersionIds,
      },
    });
    if (!result.data) {
      throw new Error('Failed to create questionnaire invites');
    }

    return result.data;
  };

  return {
    insertQuestionnaireInvites,
    loading: trpcEnabled ? trpcMutation.loading : graphqlState.loading,
    error: trpcEnabled ? trpcMutation.error : graphqlState.error,
  };
};
